import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';

import { UntilDestroy } from '@ngneat/until-destroy';
import { provideComponentStore } from '@ngrx/component-store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { Mapper } from 'tiime-expert-utils';
import { ContainerState, DialogBase, PaginationRange } from 'tiime-material';

import { Document } from '@models/document';
import { DocumentCategory } from '@models/document-category';
import { DocumentsTableComponent } from '@modules/company/documents-and-editions/documents/documents-core/components/documents-table/documents-table.component';
import { ColumnNames } from '@modules/company/documents-and-editions/documents/documents.columns';
import { DocumentsFilters, DocumentsStore } from '@modules/company/documents-and-editions/documents/documents.store';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-add-file-from-documents-dialog',
  templateUrl: './add-file-from-documents-dialog.component.html',
  styleUrls: ['./add-file-from-documents-dialog.component.scss'],
  providers: [provideComponentStore(DocumentsStore)],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddFileFromDocumentsDialogComponent extends DialogBase<
  AddFileFromDocumentsDialogComponent,
  Document | null
> {
  @ViewChild(DocumentsTableComponent) documentsTableComponent: DocumentsTableComponent;

  documentCategoryFilter = new FormControl<number | null>(null);

  readonly storeContainerState$: Observable<ContainerState> = this.documentsStore.containerState$;
  readonly documents$: Observable<Document[]> = this.documentsStore.data$.pipe(
    tap(() => this.documentsTableComponent?.tableAnchorComponent?.scrollTo())
  );
  readonly range$: Observable<PaginationRange> = this.documentsStore.pagination$;
  readonly storeIsLoading$: Observable<boolean> = this.documentsStore.isLoading$;
  readonly documentCategories$: Observable<DocumentCategory[]> = this.documentsStore.documentsCategories$;
  readonly displayedColumns: ColumnNames[] = [ColumnNames.type, ColumnNames.createdAt, ColumnNames.name];

  private readonly subscription = new Subscription();

  readonly mapToDisabledPaginator: Mapper<ContainerState, boolean> = (containerState: ContainerState) =>
    containerState !== ContainerState.done;
  readonly mapToDisabledSearchBar: Mapper<ContainerState, boolean> = (containerState: ContainerState) =>
    containerState !== ContainerState.done && containerState !== ContainerState.noResult;
  readonly mapToDisabledSort: Mapper<ContainerState, boolean> = (containerState: ContainerState) =>
    containerState !== ContainerState.done;

  constructor(
    private documentsStore: DocumentsStore,
    dialogRef: MatDialogRef<AddFileFromDocumentsDialogComponent>
  ) {
    super(dialogRef);
    this.observeDocumentsFilters();
    this.loadDocumentCategories();
  }

  applyFilter(filter: string): void {
    this.documentsStore.setSearch(filter);
  }

  applyRange(pagination: PaginationRange): void {
    this.documentsStore.setPagination(pagination);
  }

  refreshList(): void {
    this.documentsStore.forceRefresh();
  }

  applySort(sort: Sort): void {
    this.documentsStore.setSort(sort);
  }

  selectDocument(document: Document): void {
    this.close(document);
  }

  private loadDocumentCategories(): void {
    this.documentsStore.loadDocumentCategories();
  }

  private observeDocumentsFilters(): void {
    this.subscription.add(
      this.documentCategoryFilter.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap((categoryId: number) => {
            const filters: DocumentsFilters = {
              category: categoryId
            };
            this.documentsStore.setFilters(filters);
          })
        )
        .subscribe()
    );
  }
}
